// Spot-clean dispatch runner.
//
// Module-level ON PURPOSE, not component state. A dispatch is:
//
//   set suction → set water → wait ~10 s → vacuum.clean_area
//
// and that ten second gap is not padding. Both live schedule automations
// carry the same delay with the note "let the water-flow change reach
// the device before dispatching the clean". If this ran inside the
// component, closing the takeover during those ten seconds would leave
// the robot's suction and water settings rewritten with no clean ever
// dispatched — a silent half-application of a mode change that then
// leaks into the next scheduled run. Living above the component means
// the sequence finishes whether or not anyone is looking at it.
//
// It does NOT survive the tab being discarded. Nothing in a PWA does.
// Ten seconds is a risk worth taking; an hour would not have been, which
// is why sequential vac-then-mop was ruled out (see policy.js MODES).

import { VACUUM, modeById, isRunningSegment, AREA_IDS } from './policy.js';

const PRE_ROLL_MS = 10000;   // matches both schedule automations
const CONFIRM_MS  = 40000;   // give up waiting for the robot to report moving
const POLL_MS     = 500;

// Two references, deliberately:
//   `run`  — the mutable run record the async function holds. Cancellation
//            flips a flag on THIS, so it must survive publishing.
//   `job`  — the immutable snapshot React renders from.
// Collapsing them into one was the first version's bug: publishing a new
// snapshot object made `run.aborted` unreachable from abortJob, so "Send
// home" during the pre-roll cancelled nothing and the robot set off ten
// seconds later.
let run = null;
let job = null;
const subs = new Set();

function publish(next) { job = next; subs.forEach((fn) => fn(job)); }

export function subscribeJob(fn) {
  subs.add(fn);
  fn(job);
  return () => subs.delete(fn);
}

export function getJob() { return job; }
export function jobActive() { return run != null; }

// Recall has to cancel a pending dispatch as well as move the robot.
// Without this, "Send home" during the pre-roll sends it home and then
// starts a clean ten seconds later.
export function abortJob() {
  if (!run) return false;
  run.aborted = true;
  run = null;
  publish(null);
  return true;
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Run a spot clean. Never throws: a rejected service call already raises
 * the panel's own error toast, so failures here only shape local copy.
 *
 * @param hass   the panel's hass facade
 * @param areas  HA area IDs
 * @param modeId 'vacuum' | 'mop' | 'both'
 */
export async function startSpotClean(hass, areas, modeId) {
  if (run) return;                                  // one robot, one job

  // Guard the area IDs here rather than trusting the caller. clean_area
  // dispatches every mapped area and THEN raises for unmapped ones, so a
  // single bad id produces an error toast while the robot is already
  // driving. Better to never send one.
  const ids = (areas || []).filter((a) => AREA_IDS.has(a));
  if (!ids.length) return;

  const mode = modeById(modeId);
  const mine = { aborted: false };
  run = mine;

  const snapshot = (phase, error = null) => ({
    phase, error, areas: ids, mode: mode.id,
    startedAt: Date.now(), until: Date.now() + PRE_ROLL_MS,
  });
  const base = snapshot('preparing');
  publish(base);

  try {
    // Both axes, always. Suction and water persist after a run and are
    // global device state, so a mode that sets only the axis it cares
    // about poisons whatever runs next. ha-vacuum's two automations
    // adopted the same rule on 2026-08-11.
    await hass.callService('vacuum', 'set_fan_speed',
      { fan_speed: mode.fan }, { entity_id: VACUUM.entity });
    if (mine.aborted) return;
    await hass.callService('select', 'select_option',
      { option: mode.mop }, { entity_id: VACUUM.mopSelect });
    if (mine.aborted) return;

    await wait(PRE_ROLL_MS);
    if (mine.aborted) return;

    publish({ ...base, phase: 'dispatching' });
    await hass.callService('vacuum', 'clean_area',
      { cleaning_area_id: ids }, { entity_id: VACUUM.entity });
    if (mine.aborted) return;

    // Verify by behaviour, not by the call resolving. A resolved service
    // call says the message was accepted, never that the robot moved.
    publish({ ...base, phase: 'confirming' });
    const deadline = Date.now() + CONFIRM_MS;
    while (Date.now() < deadline) {
      if (mine.aborted) return;
      if (isRunningSegment(hass?.states?.[VACUUM.status]?.state)) return;
      await wait(POLL_MS);
    }
    // Fell through the deadline without the robot reporting a segment
    // run. Say so rather than clearing quietly — a spot clean that never
    // started should not look identical to one that finished.
    publish({ ...base, phase: 'confirming', error: 'timeout' });
    await wait(3000);
  } catch (err) {
    if (!mine.aborted) {
      publish({ ...base, phase: 'confirming', error: String(err?.message || err) });
      await wait(3000);
    }
  } finally {
    // Only clear if this run is still the current one. An abort already
    // cleared it and may have started nothing else; a later run must not
    // have its state wiped by an earlier one finishing late.
    if (run === mine) { run = null; publish(null); }
  }
}

export const _internals = { PRE_ROLL_MS, CONFIRM_MS, POLL_MS };
